import React, { useState, useEffect, useCallback } from 'react';
import { LanguageProvider } from './i18n';
import './styles/App.css';
import BootScreen from './components/Desktop/BootScreen';
import Desktop from './components/Desktop/Desktop';
import TerminalEaster from './components/TerminalEaster';
import Styleguide from './components/Styleguide/Styleguide';
import About from './components/About';
import ExperienceSection from './components/ExperienceSection';
import Skills from './components/Skills';
import ProjectsSection from './components/Projects/ProjectsSection';
import CertificatesSection from './components/Certificates/CertificatesSection';
import ContactSection from './components/Contact/ContactSection';
import ResumeWindow from './components/Desktop/ResumeWindow';
import RoomScene from './components/Room/RoomScene';
import CommandPalette from './components/CommandPalette/CommandPalette';
import Dock from './components/Desktop/Dock';
import MobileFallback from './components/Mobile/MobileFallback';
import { hydrateWindowsStore, useWindowsStore } from './store/windowsStore';
import { useGlobalShortcuts } from './hooks/useKeyboardShortcuts';
import { useMediaQuery } from './hooks/useMediaQuery';

const BOOT_SEEN_KEY = 'pedro_boot_seen';
const WALLPAPER_KEY = 'pedro_wallpaper_mode';
const STYLEGUIDE_HASH = '#/styleguide';

function readHash() {
  return typeof window !== 'undefined' ? window.location.hash : '';
}

function App() {
  const [route, setRoute] = useState(readHash);
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [bootTs, setBootTs] = useState(() => Number(sessionStorage.getItem('pedro_boot_ts')) || null);
  const [wallpaperMode, setWallpaperMode] = useState(
    () => sessionStorage.getItem(WALLPAPER_KEY) || 'matrix'
  );
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [viewMode, setViewMode] = useState('room');
  const [isFirstEntrance, setIsFirstEntrance] = useState(true);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const toggleWindow = useWindowsStore((s) => s.toggleWindow);
  const closeWindow = useWindowsStore((s) => s.closeWindow);

  // Hidratação manual da store — evita loop de rehydrate (zustand#1149)
  useEffect(() => {
    hydrateWindowsStore();
  }, []);

  useEffect(() => {
    const onHash = () => setRoute(readHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const handleBootDone = useCallback(() => {
    const ts = Date.now();
    sessionStorage.setItem(BOOT_SEEN_KEY, '1');
    sessionStorage.setItem('pedro_boot_ts', String(ts));
    setBootTs(ts);
    setBooting(false);
  }, []);

  const openTerminal  = useCallback(() => setTerminalOpen(true),  []);
  const closeTerminal = useCallback(() => setTerminalOpen(false), []);

  const toggleWallpaper = useCallback(() => {
    setWallpaperMode((m) => {
      const next = m === 'matrix' ? 'grid' : 'matrix';
      sessionStorage.setItem(WALLPAPER_KEY, next);
      return next;
    });
  }, []);

  const toggleLofi = useCallback(() => toggleWindow('lofi'), [toggleWindow]);

  // Terminal event: terminal:wallpaper { detail: 'matrix'|'grid' }
  useEffect(() => {
    const onTermWp = (e) => {
      const mode = e.detail;
      if (mode === 'matrix' || mode === 'grid') {
        setWallpaperMode(mode);
        sessionStorage.setItem(WALLPAPER_KEY, mode);
      }
    };
    window.addEventListener('terminal:wallpaper', onTermWp);
    return () => window.removeEventListener('terminal:wallpaper', onTermWp);
  }, []);

  // Esc → fecha terminal aberto OU janela em foco (topZ)
  const handleEscape = useCallback(() => {
    if (terminalOpen) {
      closeTerminal();
      return;
    }
    const state = useWindowsStore.getState();
    const topId = Object.entries(state.windows)
      .filter(([, w]) => w.isOpen && !w.isMinimized && !w.pinned)
      .sort((a, b) => b[1].z - a[1].z)[0]?.[0];
    if (topId) closeWindow(topId);
  }, [terminalOpen, closeTerminal, closeWindow]);

  // Atalhos globais: Cmd+K palette, Cmd+M wallpaper, Cmd+L lofi, Cmd+` terminal, Esc fecha
  useGlobalShortcuts({
    onTogglePalette: () => setPaletteOpen((v) => !v),
    onToggleWallpaper: toggleWallpaper,
    onToggleLofi: toggleLofi,
    onToggleTerminal: openTerminal,
    onEscapePressed: handleEscape,
    paletteOpen,
  });

  const windowContent = {
    about: <About />,
    projects: <ProjectsSection />,
    resume: <ResumeWindow />,
    skills: <Skills />,
    experience: <ExperienceSection />,
    certificates: <CertificatesSection />,
    contact: <ContactSection />,
  };

  // CommandPalette actions → call local toggles
  const onPaletteAction = useCallback((action) => {
    if (action === 'wallpaper') toggleWallpaper();
    else if (action === 'lofi') toggleLofi();
    else if (action === 'terminal') openTerminal();
  }, [toggleWallpaper, toggleLofi, openTerminal]);

  return (
    <LanguageProvider>
      <a href="#main" className="skip-link">Skip to main content</a>
      {route === STYLEGUIDE_HASH && <Styleguide />}
      {route !== STYLEGUIDE_HASH && isMobile && <MobileFallback />}

      {!isMobile && (
        <div id="main" className="relative min-h-screen">
          {viewMode === 'room' ? (
            <RoomScene
              initialEntrance={isFirstEntrance}
              onEnterOS={() => {
                setIsFirstEntrance(false);
                setViewMode('os');
              }}
            />
          ) : (
            <>
              <Desktop
                wallpaperMode={wallpaperMode}
                bootTimestamp={bootTs}
                onOpenTerminal={openTerminal}
                onToggleWallpaper={toggleWallpaper}
                onLeaveOS={() => setViewMode('room')}
              >
                {windowContent}
              </Desktop>

              <Dock />
            </>
          )}

          {terminalOpen && (
            <TerminalEaster onClose={closeTerminal} />
          )}

          <CommandPalette
            open={paletteOpen}
            onClose={() => setPaletteOpen(false)}
            onAction={onPaletteAction}
          />
        </div>
      )}
    </LanguageProvider>
  );
}

export default App;
